import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchProducts, getImageUrl } from '../api/api';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { useCurrency } from '../context/CurrencyContext';
import './ProductDetail.css';

function getImages(product) {
  if (product.images?.length) return product.images;
  return [product.image].filter(Boolean);
}

// Parse description into spec list with key-value pairs
function parseSpecs(description) {
  return description.split(/,\s*/).map(s => {
    const trimmed = s.trim();
    // Check if it contains a colon (key: value format)
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex > 0) {
      return {
        key: trimmed.substring(0, colonIndex).trim(),
        value: trimmed.substring(colonIndex + 1).trim()
      };
    }
    // No colon, return as plain text
    return { text: trimmed };
  }).filter(s => s.key || s.text);
}

function ProductDetail() {
  const { category, model } = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState({ active: false, x: 0, y: 0 });
  const imgRef = React.useRef(null);

  const handleMouseMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x, y });
  };

  const handleMouseLeave = () => setZoom({ active: false, x: 0, y: 0 });

  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [toast, setToast] = useState(false);
  const { addToCart, addToCartSilent } = useCart();
  const { user } = useUser();

  const handleAddToCart = () => {
    addToCart(product, qty);
    setAdded(true);
    setToast(true);
    setTimeout(() => setAdded(false), 2000);
    setTimeout(() => setToast(false), 3000);
  };

  useEffect(() => {
    fetchProducts()
      .then(data => {
        const modelLower = (model || '').toLowerCase();
        const modelSlug = modelLower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const found = data.find(p => {
          if (!p) return false;
          const pModel = (p.model || '').toLowerCase();
          const pName = (p.name || '').toLowerCase();
          const pNameSlug = pName.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          const pId = p._id || p.id || '';
          return (
            pModel === modelLower ||
            pModel === modelSlug ||
            pId === model ||
            pName === modelLower ||
            pNameSlug === modelSlug
          );
        });
        if (found) {
          console.log('Product loaded:', found);
          console.log('Category:', found.category);
          console.log('SubCategory:', found.subCategory);
          console.log('Compatible Products:', found.compatibleProducts);
          
          setProduct(found);
          
          // If it's a spare part with compatible products, fetch those products
          if (found.category === 'spare-parts' && found.compatibleProducts?.length > 0) {
            console.log('Fetching compatible products for spare part...');
            fetchProducts().then(allProducts => {
              console.log('All products fetched:', allProducts.length);
              const compatible = allProducts.filter(p => 
                found.compatibleProducts.includes(p._id || p.id)
              );
              console.log('Compatible products found:', compatible);
              setProduct(prev => ({ ...prev, _compatibleProductsData: compatible }));
            }).catch(err => {
              console.error('Error fetching compatible products:', err);
            });
          }
        } else {
          navigate(`/products?category=${category || 'all'}`, { replace: true });
        }
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        navigate('/products', { replace: true });
      });
  }, [model, category, navigate]);

  if (!product) return <div className="pd-loading">Loading...</div>;

  const images = getImages(product);
  const specs = parseSpecs(product.description);
  const price = Number(product.price || 0);
  const stockCount = product.stockCount;
  const outOfStock = product.inStock === false || stockCount === 0;
  const oldPrice = product.oldPrice > 0 ? Number(product.oldPrice) : 0;
  const hasDiscount = oldPrice > 0 && oldPrice > price;
  const save = hasDiscount ? (oldPrice - price) : 0;
  const categoryLabel = product.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const isSparepart = product.category === 'spare-parts';
  const subCategoryLabel = product.subCategory ? product.subCategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div className="pd-toast">
          <span className="pd-toast-icon">✅</span>
          <span>{product.name} added to cart!</span>
        </div>
      )}

      <div className="pd-page">
        {/* Breadcrumb */}
        <div className="pd-breadcrumb">
          <div className="container">
            <Link to="/">Home</Link>
            <span> › </span>
            <Link to={`/products?category=${product.category}`}>{categoryLabel}</Link>
            <span> › </span>
            <span>{product.name}</span>
          </div>
        </div>

        <div className="container">
          <div className="pd-layout">

            {/* Left: images */}
            <div className="pd-images">
              <div
                className={`pd-main-img${zoom.active ? ' zoomed' : ''}`}
                ref={imgRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={zoom.active ? { '--zx': `${zoom.x}%`, '--zy': `${zoom.y}%` } : {}}
              >
                {images.length > 1 && (
                  <button className="pd-arrow pd-arrow-left" onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}>&#8249;</button>
                )}
                <img
                  src={getImageUrl(images[activeImg])}
                  alt={product.name}
                  onError={e => { e.target.src = '/images/scooter-1.png'; }}
                />
                {images.length > 1 && (
                  <button className="pd-arrow pd-arrow-right" onClick={() => setActiveImg(i => (i + 1) % images.length)}>&#8250;</button>
                )}
              </div>
              <div className="pd-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`pd-thumb${activeImg === i ? ' active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={getImageUrl(img)} alt="" onError={e => { e.target.src = '/images/scooter-1.png'; }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: info */}
            <div className="pd-info">
              <div className="pd-pricing">
                <span className="pd-price">{currency} {price.toFixed(2)}</span>
                {hasDiscount && (
                  <div className="pd-price-secondary">
                    <span className="pd-old-price">{currency} {oldPrice.toFixed(2)}</span>
                    <span className="pd-save">Save: {currency} {save.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <h1>{product.name}</h1>

              {isSparepart && subCategoryLabel && (
                <div style={{ background: '#f0f7ff', border: '1px solid #b3d9ff', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0066cc', marginBottom: 4 }}>📦 Spare Part Category</div>
                  <div style={{ fontSize: 14, color: '#333' }}>Compatible with: <strong>{subCategoryLabel}</strong></div>
                </div>
              )}

              {isSparepart && !subCategoryLabel && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f57c00', marginBottom: 4 }}>⚠️ Spare Part</div>
                  <div style={{ fontSize: 13, color: '#5d4037' }}>Subcategory not specified. Please update in admin panel.</div>
                </div>
              )}

              {isSparepart && product._compatibleProductsData?.length > 0 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#16a34a', marginBottom: 8 }}>✓ Compatible Products ({product._compatibleProductsData.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {product._compatibleProductsData.map(p => (
                      <Link
                        key={p._id || p.id}
                        to={`/products/${p.category}/${p.model}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#fff', borderRadius: 6, border: '1px solid #d1fae5', textDecoration: 'none', color: '#333', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#86efac'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d1fae5'; }}
                      >
                        {p.image && (
                          <img
                            src={p.image}
                            alt={p.name}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{p.name}</div>
                          {p.brand && <div style={{ fontSize: 11, color: '#6b7280' }}>{p.brand}</div>}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>→</div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to={`/products?category=spare-parts&brand=${product.subCategory || 'all'}`}
                    style={{ display: 'inline-block', marginTop: 12, padding: '8px 16px', background: '#16a34a', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600, transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; }}
                  >
                    View All {subCategoryLabel} Spare Parts →
                  </Link>
                </div>
              )}

              {isSparepart && product.compatibleProducts?.length > 0 && !product._compatibleProductsData?.length && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#16a34a', marginBottom: 4 }}>✓ Compatible Products</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Loading compatible products...</div>
                </div>
              )}

              <ul className="pd-specs">
                {specs.map((spec, i) => (
                  <li key={i}>
                    <span className="pd-check">✓</span>
                    {spec.key ? (
                      <span>
                        {spec.key}: <strong>{spec.value}</strong>
                      </span>
                    ) : (
                      <span>{spec.text}</span>
                    )}
                  </li>
                ))}
              </ul>

              {stockCount != null && stockCount > 0 && stockCount <= 10 && (
                <p className="pd-stock-low">Only {stockCount} left in stock!</p>
              )}
              {stockCount != null && stockCount > 10 && (
                <p className="pd-stock-ok">In stock: {stockCount} available</p>
              )}

              <div className="pd-cart-row">
                {outOfStock ? (
                  <button className="pd-buy-btn pd-soldout" disabled>OUT OF STOCK</button>
                ) : (
                  <>
                    <div className="pd-qty">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                      <span>{qty}</span>
                      <button onClick={() => setQty(q => Math.min(stockCount ?? Infinity, q + 1))}>+</button>
                    </div>
                    <div className="pd-buttons">
                      <button
                        className={`pd-buy-btn ${added ? 'pd-added' : ''}`}
                        onClick={handleAddToCart}
                      >
                        {added ? '✓ Added!' : '🛒 ADD TO CART'}
                      </button>
                      <button
                        className="pd-buynow-btn"
                        onClick={() => { addToCartSilent(product, qty); navigate('/cart?checkout=true'); }}
                      >
                        ⚡ BUY NOW
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default ProductDetail;
